/* =====================================================================================
   The criteria builder with its own results table, for a screen that is entirely a search.

   The editor itself now lives in CriteriaPanel, because the Atlas needed to own the criteria and
   feed them to a map, a table and an export at once. This component is the self-contained form:
   it holds its own criteria and renders its own answer, which is what Circus wants, where
   searching the competitor set IS the page rather than a lens over something already on screen.

   Both call the same runSearch and the same editor, so must and prefer behave identically
   wherever you meet them.
   ===================================================================================== */

import { useMemo, useState } from 'react';
import { runSearch, type Criterion, type Hit, type SearchField } from '../lib/search';
import { CriteriaPanel, ResultCount, type Preset } from './CriteriaPanel';
import './searchbuilder.css';

export function SearchBuilder<T extends Record<string, unknown>>({
  rows, fields, presets, renderRow, columns, fallbackSort, onCount,
}: {
  rows: T[];
  fields: SearchField<T>[];
  presets?: Preset<T>[];
  renderRow: (hit: Hit<T>, index: number) => React.ReactNode;
  columns: string[];
  fallbackSort?: { field: string; direction: 'asc' | 'desc' };
  onCount?: (shown: number, total: number) => void;
}) {
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [text, setText] = useState('');
  const [limit, setLimit] = useState(100);
  const [showWhy, setShowWhy] = useState<number | null>(null);

  const result = useMemo(
    () => runSearch(rows, fields, criteria, { limit, text, fallbackSort }),
    [rows, fields, criteria, limit, text, fallbackSort],
  );

  // Reported upward rather than rendered here, so the host page can put it in its own header.
  useMemo(() => onCount?.(result.hits.length, result.total), [result, onCount]);

  const ranked = criteria.some((c) => c.mode === 'prefer');

  return (
    <div>
      <CriteriaPanel<T>
        fields={fields} criteria={criteria} setCriteria={setCriteria}
        presets={presets} text={text} setText={setText}
        extra={
          <label className="sb-limit">
            Return
            <input className="input" type="number" min={1} max={2000} value={limit}
                   onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || 1))} />
            best fit
          </label>
        }
      />

      <ResultCount shown={result.hits.length} total={result.total} criteria={criteria}
                   excludedBy={result.excludedBy} fields={fields} />

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th className="num">#</th>
              {columns.map((c) => <th key={c}>{c}</th>)}
              {ranked && <th className="num">Fit</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {result.hits.map((h, i) => (
              <>
                <tr key={i} className="sb-hit">
                  <td className="num muted">{i + 1}</td>
                  {renderRow(h, i)}
                  {ranked && (
                    <td className="num">
                      <span className="sb-score" style={{ opacity: 0.35 + (h.score / 100) * 0.65 }}>
                        {h.score.toFixed(0)}
                      </span>
                    </td>
                  )}
                  <td className="num">
                    <button className="link-btn"
                            onClick={() => setShowWhy(showWhy === i ? null : i)}>
                      {showWhy === i ? 'hide' : 'why'}
                    </button>
                  </td>
                </tr>
                {showWhy === i && (
                  <tr key={`${i}-why`} className="sb-why">
                    <td colSpan={columns.length + 3}>
                      {h.reasons.map((r) => (
                        <span key={r.field} className={`sb-reason ${r.met ? 'met' : 'unmet'}`}>
                          {r.label}
                          {r.note === 'required' ? ' (required)'
                            : r.met ? ` +${r.contribution}${r.note === 'partial' ? ' partial' : ''}`
                            : ' no'}
                        </span>
                      ))}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
